// The second-touch draft check -- the CRM's copy (SECOND-TOUCH-2026-10-02 §2, §4).
//
// pt-agents' agents/draft_check.py checks every draft when it is written; this file runs
// the SAME rules when Matt edits one here, so an edited draft is re-checked on save and a
// failing draft is never offered as sendable. Both repos run the same fixture file
// (draftCheckCases.json, a copy of pt-agents tests/fixtures/draft_check_cases.json) and
// draftCheck.test.js fails if the two copies -- or the two facts files -- drift apart.
//
// The facts (business line, attested support line, not-attested phrases, competitor
// names) come only from secondTouchFacts.json, a copy of pt-agents
// agents/data/second_touch_facts.json, which is itself synced from the single sources.
import FACTS from './secondTouchFacts.json';

export const STOP_FOOTER = 'Reply STOP to opt out.';
export const SMS_MAX_SEGMENTS = 2;
export const EMAIL_MAX_WORDS = 120;
const SITE = 'https://www.protherapystaffing.com/';

// Ported from recruiter-agent src/brain.js unattestedClaim (EVENING-2026-10-01 §1).
const ROUND_THE_CLOCK =
  /24\s*[/x-]\s*7|\b(?:a)?round[-\s]the[-\s]clock\b|\btwenty[-\s]four[-\s](?:seven|hours?\s+a\s+day)\b|\b24[-\s]hours?\s+a\s+day\b/i;
const NOT_OURS = String.raw`(?:dental|vision|life\s+insurance|sign[-\s]on\s+bonus(?:es)?|completion\s+bonus(?:es)?|401\(?k\)?\s+match(?:ing)?|employer\s+match|tuition)`;
const NOT_OURS_NEG = new RegExp(String.raw`(?:\bnot?\b|\bdon'?t\b|\bdoes\s?n'?t\b|\bnever\b|\bwithout\b)[^.!?]{0,40}` + NOT_OURS, 'i');
const NOT_OURS_AFF = new RegExp(String.raw`\b(?:we|protherapy)\s+(?:also\s+)?(?:do\s+)?(?:offer|provide|include|give|have|pay)\b[^.!?]{0,50}` + NOT_OURS, 'i');

const MONEY = /\$\s?[0-9][0-9,]*/;
const PAY_UNIT = /\b(?!401\s*k\b)[0-9][0-9,.]*\s*(?:k\b|\/\s*(?:wk|week|hr|hour)\b|(?:per|a|an)\s+(?:week|hour)\b|weekly\b|hourly\b)/i;
const PHONE = /(?:\+?1[\s.-]?)?\(?\b[2-9][0-9]{2}\)?[\s.-]?[0-9]{3}[\s.-]?[0-9]{4}\b/g;
const URL_RE = /https?:\/\/[^\s<>"')]+/gi;
// Discipline must match (ruled at the GATE, 2026-10-02): never assistant openings to a PT lead,
// never PT openings to an assistant lead. The same two patterns as agents/draft_check.py.
const ASSISTANT = /\bPTA\b|\bassistants?\b/i;
const PT_ONLY = /\bPT\b|physical\s+therapists?\b(?!\s+assistant)/i;
const PLACEHOLDER = /\{[a-z_]+\}/;

const GSM_BASIC = new Set(
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
);
const GSM_EXT = new Set('^{}\\[~]|€');

export const facts = FACTS;

/** (AAA) EEE-LLLL -- the same shape as the flagship's displayNumber(). */
export function displayLine(e164 = FACTS.business_line_e164) {
  const d = e164.slice(2);
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

export function smsSegments(text) {
  const chars = Array.from(String(text || ''));
  if (chars.every((ch) => GSM_BASIC.has(ch) || GSM_EXT.has(ch))) {
    const units = chars.reduce((n, ch) => n + (GSM_EXT.has(ch) ? 2 : 1), 0);
    return units <= 160 ? 1 : Math.ceil(units / 153);
  }
  const units = chars.reduce((n, ch) => n + (ch.codePointAt(0) > 0xffff ? 2 : 1), 0);
  return units <= 70 ? 1 : Math.ceil(units / 67);
}

const digits10 = (s) => s.replace(/\D/g, '').slice(-10);
const questionRule = (body) => (body.match(/\?/g) || []).length === 1 && body.trimEnd().endsWith('?');
const escapeRe = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** [body, signature]: the signature is the trailing block after the last blank line. */
export function emailParts(text) {
  const t = String(text || '').trim();
  const i = t.lastIndexOf('\n\n');
  return i >= 0 ? [t.slice(0, i), t.slice(i + 2)] : [t, ''];
}

/** The list of failed rule ids (empty = passes). channel: 'sms' | 'email'. */
export function checkDraft(text, channel, subject = null, f = FACTS, discipline = null) {
  const t = String(text || '');
  const fails = [];
  const stripped = t.split(f.support_247).join(' ');
  if (ROUND_THE_CLOCK.test(stripped)) fails.push('unattested_support_hours');
  if (!NOT_OURS_NEG.test(t) && NOT_OURS_AFF.test(t)) fails.push('invented_benefit');
  const low = t.toLowerCase();
  if (f.not_attested_phrases.some((p) => low.includes(p.toLowerCase()))) fails.push('not_attested_phrase');
  if (f.competitors.some((n) => new RegExp(`(?:^|[^a-z])${escapeRe(n)}(?![a-z])`).test(low))) {
    fails.push('competitor_named');
  }
  const whole = t + ' ' + (subject || '');
  const disc = String(discipline || '').trim().toUpperCase();
  if ((disc === 'PT' && ASSISTANT.test(whole)) || (disc === 'PTA' && PT_ONLY.test(whole))) fails.push('discipline_mismatch');
  const noUrls = (t + ' ' + (subject || '')).replace(URL_RE, ' ');
  if (MONEY.test(noUrls) || PAY_UNIT.test(noUrls)) fails.push('pay_number');
  const line = f.business_line_e164.slice(-10);
  const phones = (noUrls.match(PHONE) || []).map(digits10);
  if (phones.some((p) => p !== line)) fails.push('unknown_phone');
  if (!phones.includes(line)) fails.push('missing_business_line');
  if ((t.match(URL_RE) || []).some((u) => !u.startsWith(SITE))) fails.push('foreign_link');
  if (PLACEHOLDER.test(t) || PLACEHOLDER.test(subject || '')) fails.push('placeholder_left');
  if (channel === 'sms') {
    let body = t;
    if (!t.trimEnd().endsWith(STOP_FOOTER)) fails.push('sms_missing_stop');
    else body = t.trimEnd().slice(0, -STOP_FOOTER.length);
    if (!questionRule(body)) fails.push('one_question_at_end');
    if (smsSegments(t) > SMS_MAX_SEGMENTS) fails.push('sms_too_long');
  } else if (channel === 'email') {
    const [body] = emailParts(t);
    if (!questionRule(body)) fails.push('one_question_at_end');
    if (t.split(/\s+/).filter(Boolean).length > EMAIL_MAX_WORDS) fails.push('email_too_long');
    if (!String(subject || '').trim()) fails.push('email_missing_subject');
  } else {
    fails.push('unknown_channel');
  }
  return fails;
}

/** Plain-English labels for the failure ids, shown beside a failing draft. */
export const RULE_LABELS = {
  unattested_support_hours: 'support-hours wording other than the attested line',
  invented_benefit: 'offers a benefit we do not have',
  not_attested_phrase: 'a phrase marked not attested',
  competitor_named: 'names a competitor',
  discipline_mismatch: 'offers openings for the other discipline (PT vs PTA)',
  pay_number: 'a typed pay figure',
  unknown_phone: 'a phone number other than the business line',
  missing_business_line: 'the business line is missing',
  foreign_link: 'a link outside protherapystaffing.com',
  placeholder_left: 'an unfilled {placeholder}',
  one_question_at_end: 'needs exactly one question, as the last sentence',
  sms_missing_stop: `must end with "${STOP_FOOTER}"`,
  sms_too_long: 'longer than two text segments',
  email_too_long: `over ${EMAIL_MAX_WORDS} words`,
  email_missing_subject: 'no subject',
  unknown_channel: 'unknown channel',
};
